import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import ClearFiltersButton from "../components/ClearFiltersButton";
import DownloadCsvButton from "../components/DownloadCsvButton";
import { searchIcon } from "../../../assets";
import ReportsService from "../../../services/ReportsService";
import FacilityManagementService from "../../../services/FacilityManagementService";
import PatientFacilityTrailDialog from "../dialogs/PatientFacilityTrailDialog";
import {
  hideLoader,
  showLoader,
} from "../../../component-lib/Molecules/loader/LoaderSlice";
import { eventEHRCustomToastMessage } from "../../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";
import { componentKey } from "../ReportsSlice";
import { getFacilityPatientHistoryReport } from "../ReportsSaga";
import {
  ALL_FACILITIES_OPTION,
  CSV_HEADERS_FACILITY_PATIENT_HISTORY,
  ROWS_PER_PAGE_OPTIONS,
} from "../ReportConstants";

type FacilityPatientHistoryTabProps = { facilityId: string };

const FacilityPatientHistoryTab = ({
  facilityId,
}: FacilityPatientHistoryTabProps) => {
  const dispatch = useDispatch();
  const { facilityPatientHistoryReport } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>,
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [searchedValue, setSearchedValue] = useState("");
  const [seenFacility, setSeenFacility] = useState("");
  const [currentFacility, setCurrentFacility] = useState("");
  const [facilityOptions, setFacilityOptions] = useState<
    { label: string; value: string }[]
  >([ALL_FACILITIES_OPTION]);
  const [openTrail, setOpenTrail] = useState(false);
  const [trailData, setTrailData] = useState<Record<string, any> | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const rows: Array<Record<string, any>> =
    facilityPatientHistoryReport?.data || [];

  // Load the facility list once for the "Current Facility" filter.
  useEffect(() => {
    const loadFacilities = async () => {
      try {
        const response = await FacilityManagementService.getFacilityList({
          pageNumber: 1,
          limit: 100,
        });
        const facilities: Array<{ id: string; name: string }> =
          response?.data?.facilitys || [];
        setFacilityOptions([
          ALL_FACILITIES_OPTION,
          ...facilities.map((facility) => ({
            label: facility?.name,
            value: facility?.id,
          })),
        ]);
      } catch {
        setFacilityOptions([ALL_FACILITIES_OPTION]);
      }
    };
    loadFacilities();
  }, []);

  // Fetch a single patient's full facility movement trail and open the modal.
  const handleViewTrail = async (patientId: string) => {
    if (!patientId) return;
    try {
      dispatch(showLoader());
      const response =
        await ReportsService.getPatientFacilityHistory(patientId);
      const data = response?.data?.data || null;
      if (data) {
        setTrailData(data);
        setOpenTrail(true);
      } else {
        eventEHRCustomToastMessage({
          title: "Unable to load facility trail",
          status: "error",
        });
      }
    } catch (error: any) {
      eventEHRCustomToastMessage({
        title: error?.response?.data?.message || "Failed to load facility trail",
        status: "error",
      });
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    dispatch(
      getFacilityPatientHistoryReport({
        pageNumber,
        limit: rowPerPage,
        facilityId: facilityId || undefined,
        seenFacility: seenFacility || undefined,
        currentFacility: currentFacility || undefined,
        searchValue: searchedValue?.trim() || undefined,
      }),
    );
  }, [
    facilityId,
    seenFacility,
    currentFacility,
    pageNumber,
    rowPerPage,
    searchedValue,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setSearchedValue(value?.trim());
    }, 500);
  };

  const isAnyFilterApplied = Boolean(
    searchedValue || seenFacility || currentFacility,
  );

  const handleClearFilters = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setSearchedValue("");
    setSeenFacility("");
    setCurrentFacility("");
    setPageNumber(1);
  };

  const columns = [
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.patientName || "-"}</p>
      ),
    },
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.bsaId || "-"}</p>
      ),
    },
    {
      field: "currentFacilityName",
      label: <div>Current Facility</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.currentFacilityName || "-"}
        </p>
      ),
    },
    {
      field: "action",
      label: <div>Actions</div>,
      renderLogic: (row: Record<string, any>) => (
        <button
          type="button"
          className="text-[#005596] font-medium hover:underline"
          onClick={() => handleViewTrail(row?.patientId)}
        >
          View History
        </button>
      ),
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getFacilityPatientHistory({
      limit: "All",
      facilityId: facilityId || undefined,
      seenFacility: seenFacility || undefined,
      currentFacility: currentFacility || undefined,
      searchValue: searchedValue?.trim() || undefined,
    });
    const data: Array<Record<string, any>> = response?.data?.data || [];
    return data.map((row) => ({
      patientName: row?.patientName,
      bsaId: row?.bsaId,
      currentFacilityName: row?.currentFacilityName,
    }));
  };

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <SearchSelectDropdown
          freeSolo={true}
          searchedValue={searchedValue}
          onSearchValueChangeCb={handleSearchChange}
          size="small"
          label="Search by Patient Name, BSA ID"
          fullWidth={"280px"}
          startIcon={
            <img
              alt="search-icon"
              src={searchIcon}
              width={"18px"}
              height={"18px"}
            />
          }
        />
        <div style={{ minWidth: "200px" }}>
          <SelectDropdown
            label="Seen at Facility"
            value={seenFacility}
            options={facilityOptions}
            onChangeCb={(e) => {
              setPageNumber(1);
              setSeenFacility(e.target.value);
            }}
          />
        </div>
        <div style={{ minWidth: "200px" }}>
          <SelectDropdown
            label="Current Facility"
            value={currentFacility}
            options={facilityOptions}
            onChangeCb={(e) => {
              setPageNumber(1);
              setCurrentFacility(e.target.value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_FACILITY_PATIENT_HISTORY}
          filename="facility-patient-history.csv"
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage="No patient history found for this facility"
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: facilityPatientHistoryReport?.totalPages,
          totalCount: facilityPatientHistoryReport?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />

      <PatientFacilityTrailDialog
        open={openTrail}
        trail={trailData}
        handleClose={() => setOpenTrail(false)}
      />
    </div>
  );
};

export default FacilityPatientHistoryTab;
